import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import { uploadExamFile } from "../utils/api";
import styles from "./UploadQuestions.module.css";

const REQUIRED_HEADERS = [
  "type",
  "question",
  "explanation",
  "course_id",
  "right_ans",
  "ans1",
  "ans2",
  "ans3",
  "ans4",
];

const UploadQuestions = ({ showModal, onClose, examList }) => {
  const [selectedExam, setSelectedExam] = useState("");
  const [newExamName, setNewExamName] = useState("");
  const [file, setFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState("");
  const token = localStorage.getItem("token");

  useEffect(() => {
    if (showModal) {
      setSelectedExam("");
      setNewExamName("");
      setFile(null);
      setUploadStatus("");
    }
  }, [showModal]);

  // Utility: reads file as ArrayBuffer
  const readFileAsync = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = (err) => reject(err);
      reader.readAsArrayBuffer(file);
    });

  // Parse and validate the selected Excel file
  const handleFileChange = async (e) => {
    const uploadedFile = e.target.files?.[0];
    if (!uploadedFile) return;

    if (!/\.xlsx?$|\.xls$/.test(uploadedFile.name)) {
      alert("Only Excel files (.xlsx, .xls) are allowed.");
      e.target.value = null;
      return;
    }

    try {
      const data = await readFileAsync(uploadedFile);
      const workbook = XLSX.read(data, { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });

      if (!rows.length) {
        setUploadStatus("Error: The spreadsheet is empty!");
        return;
      }

      const headerRow = rows[0];
      if (headerRow.length !== REQUIRED_HEADERS.length) {
        setUploadStatus(
          `Error: Expected ${REQUIRED_HEADERS.length} headers, found ${headerRow.length}.`
        );
        return;
      }

      for (let i = 0; i < REQUIRED_HEADERS.length; i++) {
        if (headerRow[i] !== REQUIRED_HEADERS[i]) {
          setUploadStatus(
            `Error: Header mismatch in column ${i + 1} (expected "${REQUIRED_HEADERS[i]}", found "${headerRow[i]}").`
          );
          return;
        }
      }

      setFile(uploadedFile);
      setUploadStatus("File format looks correct!");
    } catch (err) {
      console.error("File parse error:", err);
      setUploadStatus("Error reading or parsing the file.");
    }
  };

  // Upload the validated file
  const handleUpload = async () => {
    if (!file) {
      setUploadStatus("Please select a valid Excel file first.");
      return;
    }
    if (!selectedExam && !newExamName.trim()) {
      setUploadStatus("Please select or enter an exam name.");
      return;
    }

    try {
      const examId = selectedExam || newExamName.trim();
      await uploadExamFile(examId, file, token);
      setUploadStatus("File uploaded successfully!");
      setTimeout(onClose, 2000);
    } catch (error) {
      console.error("Upload error:", error);
      setUploadStatus(
        `Failed to upload file. ${error.message || ""}`
      );
    }
  };

  // Don't render modal if not shown
  if (!showModal) return null;

  return (
    <div className={styles['modal-overlay']}>
      <div className={styles['modal-content']}>
        <h2 className={styles['title']}>Upload Exam Questions</h2>

        <label className={styles['label']}>New Exam Name:</label>
        <input
          type="text"
          className={styles['input']}
          placeholder="Enter new exam name"
          value={newExamName}
          onChange={(e) => setNewExamName(e.target.value)}
        />

        <p className={styles['or-text']}>OR</p>

        <label className={styles['label']}>Select Existing Exam:</label>
        <select
          className={styles['select']}
          value={selectedExam}
          onChange={(e) => setSelectedExam(e.target.value)}
        >
          <option value="">-- Select Exam --</option>
          {examList?.map((exam) => (
            <option key={exam.id} value={exam.id}>
              {exam.exam_name}
            </option>
          ))}
        </select>

        <label className={styles['label']}>Choose File:</label>
        <input
          type="file"
          className={styles['file-input']}
          accept=".xlsx, .xls"
          onChange={handleFileChange}
        />

        {uploadStatus && (
          <p className={`${styles['upload-status']} ${
            uploadStatus.toLowerCase().includes("error")
              ? styles['error']
              : styles['success']
          }`}>
            {uploadStatus}
          </p>
        )}

        <div className={styles['modal-actions']}>
          <button
            className={styles['upload-confirm-btn']}
            onClick={handleUpload}
          >
            Upload
          </button>
          <button
            className={styles['cancel-btn']}
            onClick={onClose}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadQuestions;